// The assignment function, browser half — docs/plans/ab-testing.md D1, §3.1, CS1.
//
// The twin of internal/domain/experiment/assign.go. Both are held to
// packages/api-client/fixtures/experiment-arm-cases.json, because a client that
// hashes differently from the server is a silently broken price test: the page
// shows one arm and the cart charges another.
//
// SYNCHRONOUS, and that is the whole reason this file carries its own SHA-256.
// The arm has to exist before the first paint (the theme patch is applied to the
// doc SectionList renders from), and crypto.subtle.digest returns a Promise that
// cannot be awaited before a synchronous render. So this is a compact SHA-256
// that only ever needs the first 64 bits of the digest — and it is the reason
// the entry chunk grows at all (plan §3.4; the measured cost is in the CS1
// commit body).

import { applyExperimentPatch } from "@dk/theme"
import type { ExperimentPatch, Theme } from "@dk/theme"
import type { LocationQueryRaw, RouteLocationNormalized } from "vue-router"
import { forcedArm, isPreview } from "./previewFlag"
import { sessionID } from "./visitor"

let K: number[] | undefined
let H0: number[] | undefined

// The 64 round constants and 8 initial hash values are the fractional parts of
// the cube and square roots of the first primes — derived here rather than
// written out, which is ~250 bytes of literals the entry chunk does not carry.
// A double holds the 32 fractional bits of a root below 312 exactly enough; the
// fixture and a comparison against node:crypto over thousands of inputs are
// what prove it, not this comment.
function init(): void {
  K = []
  H0 = []
  const frac = (x: number) => ((x - Math.floor(x)) * 0x100000000) | 0
  for (let n = 2, i = 0; i < 64; n++) {
    let prime = true
    for (let d = 2; d * d <= n; d++) if (n % d === 0) prime = false
    if (!prime) continue
    if (i < 8) H0[i] = frac(Math.pow(n, 1 / 2))
    K[i++] = frac(Math.pow(n, 1 / 3))
  }
}

/** SHA-256 of a string's UTF-8 bytes, as eight signed 32-bit words. */
export function sha256Words(s: string): number[] {
  if (!K) init()
  const bytes = new TextEncoder().encode(s)
  const bitLen = bytes.length * 8
  // Message + 0x80 + zero pad + 64-bit length, to a multiple of 64 bytes.
  const words = new Array<number>((((bytes.length + 8) >> 6) + 1) * 16).fill(0)
  for (let i = 0; i < bytes.length; i++) words[i >> 2] = words[i >> 2]! | (bytes[i]! << (24 - (i % 4) * 8))
  words[bytes.length >> 2] = words[bytes.length >> 2]! | (0x80 << (24 - (bytes.length % 4) * 8))
  // Lengths past 2^32 bits never occur for an id; the high length word stays 0.
  words[words.length - 1] = bitLen
  const h = H0!.slice()
  const w = new Array<number>(64)
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n))
  for (let j = 0; j < words.length; j += 16) {
    for (let i = 0; i < 64; i++) {
      if (i < 16) w[i] = words[j + i]!
      else {
        const a = w[i - 15]!
        const b = w[i - 2]!
        w[i] = (w[i - 16]! + (rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3)) + w[i - 7]! + (rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10))) | 0
      }
    }
    let [a, b, c, d, e, f, g, hh] = h as [number, number, number, number, number, number, number, number]
    for (let i = 0; i < 64; i++) {
      const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K![i]! + w[i]!) | 0
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0
      hh = g
      g = f
      f = e
      e = (d + t1) | 0
      d = c
      c = b
      b = a
      a = (t1 + t2) | 0
    }
    h[0] = (h[0]! + a) | 0
    h[1] = (h[1]! + b) | 0
    h[2] = (h[2]! + c) | 0
    h[3] = (h[3]! + d) | 0
    h[4] = (h[4]! + e) | 0
    h[5] = (h[5]! + f) | 0
    h[6] = (h[6]! + g) | 0
    h[7] = (h[7]! + hh) | 0
  }
  return h
}

/**
 * [0, 100) for (key, session): the first 8 bytes of the digest as a big-endian
 * unsigned 64-bit integer, mod 100 — computed from its two 32-bit halves without
 * BigInt, since hi·2³² + lo ≡ (hi mod 100)·96 + lo mod 100 (mod 100), 2³² mod 100
 * being 96.
 */
export function bucket(key: string, session: string): number {
  const h = sha256Words(key + ":" + session)
  return (((h[0]! >>> 0) % 100) * 96 + ((h[1]! >>> 0) % 100)) % 100
}

/**
 * The index of the variant a session sees, control first. An empty session is
 * control and is never hashed — the rule the charge side applies to a cart with
 * no session, so the two never disagree for a cookie-blocked shopper (plan D1).
 */
export function arm(key: string, session: string, splits: readonly number[]): number {
  if (!session || !splits.length) return 0
  const p = bucket(key, session)
  let acc = 0
  for (let i = 0; i < splits.length; i++) {
    acc += splits[i]!
    if (p < acc) return i
  }
  return 0
}

/** One running experiment as /bootstrap carries it (experiment.Public in Go). */
export interface PublicExperiment {
  key: string
  kind: string
  splits: number[]
  /** In arm order, control first. A theme_patch payload is an ExperimentPatch;
   *  a url_split one is `{target}`. */
  payloads: unknown[]
  /** url_split: the page whose visitors are split, query-free. */
  path?: string
}

/** The arm this visit renders: the forced one on a QA link, else the hash. */
function armOf(e: PublicExperiment, forced: { key: string; arm: number } | null): number {
  return forced ? (forced.key === e.key ? forced.arm : 0) : arm(e.key, sessionID(), e.splits)
}

/** This visit's arm of a served test: control in a preview unless the link
 *  forces one, else the forced arm or the session's. */
export function visitArm(e: PublicExperiment): number {
  const forced = forcedArm()
  if (isPreview() && !forced) return 0
  return armOf(e, forced)
}

/**
 * X-DK-Exp for a cart call: the keys of the money tests whose prices this page
 * displayed (plan D6). The cart records each the first time a write carries
 * it and is charged the arm only for keys it recorded — so a browser still
 * rendering a bootstrap from before a test started is charged list price, the
 * price it showed. A preview displays control and sends nothing.
 */
export function moneyHeader(exps: PublicExperiment[] | undefined): string {
  if (!exps?.length || (isPreview() && !forcedArm())) return ""
  return exps
    .filter((e) => e.kind === "price" || e.kind === "shipping")
    .map((e) => e.key)
    .join(",")
}

/** The part of a route a URL split reads. */
type SplitFrom = Pick<RouteLocationNormalized, "path" | "hash" | "query" | "redirectedFrom">

/**
 * Where a URL split test sends this visit (plan CS5): the location that replaces
 * `to`, or undefined to stay. The page is matched EXACTLY, query-free — the same
 * rule the report's exposure matcher applies to the recorded path, so a visit the
 * browser did not split is one the report does not count (D3).
 *
 * The shopper's own query (utm_*, fbclid, a discount code) rides along, with the
 * target's own parameters winning a clash; the hash stays. No `replace`: the split
 * page's URL never enters history (the navigation to it never commits), a first
 * load already replaces its landing entry, and `replace` on an in-store click would
 * overwrite the page the shopper came FROM — Back would skip it. One hop only: a
 * location a split already redirected to is never split again — the server
 * refuses two live splits that share a page, and this is the browser half.
 *
 * Preview renders control unless the link forces an arm, as experimentDoc does.
 */
export function splitLocation(
  to: SplitFrom,
  exps: PublicExperiment[] | undefined,
): { path: string; query: LocationQueryRaw; hash: string } | undefined {
  const forced = forcedArm()
  if (!exps?.length || (isPreview() && !forced)) return
  const find = (path: string) => exps.find(e => e.kind === "url_split" && e.path === path)
  if (to.redirectedFrom && find(to.redirectedFrom.path)) return
  const e = find(to.path)
  const target = e && (e.payloads[armOf(e, forced)] as { target?: string } | undefined)?.target
  if (!target) return
  const u = new URL(target, "http://x")
  return {
    path: u.pathname,
    query: { ...to.query, ...Object.fromEntries(u.searchParams) },
    hash: to.hash || u.hash,
  }
}

/**
 * The theme document this visit renders: the published doc with this session's
 * arm of every running theme_patch test applied — on every route, whatever the
 * test's surface, because the surface decides exposure and never serving (D2). A
 * patch on the header that applied only on product pages would change the page
 * between two navigations of one visit.
 *
 * Synchronous, from the cached bootstrap, so the variant is the FIRST paint in
 * the steady state. The base is never mutated: it is the cached payload, and the
 * next revalidation compares against it. A store running no test gets `base`
 * back untouched and pays one array check.
 *
 * Preview (the builder, a theme preview, a QA link) renders control for every
 * test unless the link forces one arm (`dk_arm`), so a seller editing their
 * theme never edits through a random variant.
 */
export function experimentDoc(base: Theme, exps: PublicExperiment[] | undefined): Theme {
  const forced = forcedArm()
  if (!exps?.length || (isPreview() && !forced)) return base
  let doc: Theme | undefined
  for (const e of exps) {
    if (e.kind !== "theme_patch") continue
    const i = armOf(e, forced)
    const patch = e.payloads[i] as ExperimentPatch | undefined
    if (!i || !patch?.ops?.length) continue
    doc ??= JSON.parse(JSON.stringify(base)) as Theme
    applyExperimentPatch(doc, patch)
  }
  return doc ?? base
}
